/**
 * Where the facts come from: each source the graph draws on, what is taken
 * from it, what is deliberately not taken, how often it is read, and the
 * terms under which it is read. Written for the researcher who asks which
 * records a number rests on, and for the journalist who asks whether a page
 * was republished. Each source's terms are the catalog's; this page sets
 * them out in words.
 */
import type { DocPage } from '../types.js';

export const sourcesPage: DocPage = {
  slug: 'sources',
  title: 'Where the facts come from',
  audience: 'researchers, journalists',
  promise: 'Each source the graph reads, what is taken from it and what is not, how often, and under what terms.',
  lead: 'Every fact on the site came from one of six sources, and every fact names which. This page describes each source, what the graph takes from it, what it leaves out on purpose, and the terms it is read under.',
  sections: [
    {
      id: 'returns',
      heading: 'The e-filed returns',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'The IRS publishes every e-filed Form 990, 990-EZ, and 990-PF as XML, in monthly archives on its own site, about a month after the returns are processed. FunderGraph collects the archives the IRS lists, keeps each with its size and hash, and reads them through one concordance that maps every schema version the IRS has used to one set of fields. From a return it takes the header (the EIN, the name, the city and state, the tax period, the return type), the mission and program descriptions, the Schedule O narratives, Schedule I (grants to organizations in the United States), and, from a 990-PF, Part XV (grants and contributions paid). It does not take Schedule F (grants outside the United States), Schedule B (the list of contributors), or any officer, director, or compensation line, because the first is out of scope and the others name people.',
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'The returns are public records, published by the IRS under the law that opens them, and FunderGraph treats them as public domain. A copy of every return the site cites is linked beside the fact, at ProPublica’s Nonprofit Explorer, so a reader can see the filing as the IRS released it.',
          ],
        },
      ],
    },
    {
      id: 'index',
      heading: 'The e-file index',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'Beside the archives the IRS publishes an index of the e-filed returns by year: one row per return with its object identifier, the filer’s EIN and name, the tax period, the return type, and the archive it is in. The index tells the collector which archives exist and tells the reader which return an object identifier names. It is read with the archives, monthly, and carries no terms beyond theirs.',
          ],
        },
      ],
    },
    {
      id: 'master',
      heading: 'The master file of exempt organizations',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'The IRS Exempt Organizations Business Master File lists every organization currently recognized as tax-exempt, in four regional files refreshed monthly: the EIN, the name, the city and state, the subsection, the foundation code, the ruling date, the activity code, the status, the latest tax period on record, and the asset, income, and revenue ranges of the last return. It is the identity backbone of the graph: every organization’s page is minted from its EIN, and every recipient name on a grant list is matched against it. The site shows the city and state and never the street address, and the “in care of” name, a person’s name in most rows, is dropped before anything else is read.',
          ],
        },
      ],
    },
    {
      id: 'websites',
      heading: 'Funders’ and sponsors’ websites',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'Foundations and community foundations publish grant lists months before the corresponding return is posted, and fiscal sponsors publish the projects they host. A scheduled collector reads such pages under each site’s terms of use and robots directives, which are checked at every capture; a page the directives disallow is left out and named in the collection’s manifest. Only the facts read from a page are kept: the funder, the recipient as written, the amount, the year, the program, and the purpose. Page content is not republished. A grant read from a page is what the funder published on the capture date, and when a later return reports the same grant the two become one edge with two citations, which the page says.',
          ],
        },
        {
          kind: 'note',
          text: ['In the current national build no website has yet been collected for the country; the collector runs against the fixture. Every grant on the site today comes from a return.'],
        },
      ],
    },
    {
      id: 'federal',
      heading: 'Federal awards and audits',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'USAspending publishes every federal assistance award with the recipient’s name, the agency, the program, the amount, and the period; the Federal Audit Clearinghouse publishes the single audits of organizations that spend federal funds above the audit threshold, with the EIN and the programs. Both are works of the United States government. An award is a funding edge from an agency, which the site shows beside grants from filers, counted as a funder under its own name.',
          ],
        },
        {
          kind: 'note',
          text: ['In the current national build neither federal source has been collected for the country; both collectors run against the fixture, and the audit collector needs a key the project does not yet hold.'],
        },
      ],
    },
    {
      id: 'vocabulary',
      heading: 'Classification',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'Subjects come from Candid’s Philanthropy Classification System, licensed under Creative Commons Attribution 4.0 and credited in the catalog; the November 2024 workbook is the version the graph carries. The master file’s activity codes (NTEE) are mapped into that tree as subclasses, by Candid’s own former-code column where it names a code and by major group where it does not, so a subject page counts work under it and under every narrower subject. The names of the activity codes are those the National Center for Charitable Statistics publishes; the definitions are not reproduced.',
          ],
        },
      ],
    },
    {
      id: 'notused',
      heading: 'Sources the graph does not use',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'The GivingTuesday 990 data lake holds the same returns and historical archives the IRS no longer lists, under the Open Database License. Whether that license’s share-alike terms would attach to a graph built from its bytes is a question FunderGraph has not settled, so filings and master-file rows come from the IRS archives, which are public domain, and the lake is not read. Commercial databases are not read either. ProPublica’s Nonprofit Explorer is linked as the public copy of each return and is not used as a data source.',
          ],
        },
      ],
    },
  ],
};
